/**
 * The Project Intelligence panel's shared rules (Phase 31; PRD 10.5; TRD 10.2): the words for a
 * source mode, a flag, a link method and a schedule source; the sentences the panel says about
 * remaining hours, burn, slip, the baseline and the error band; the role hues and the stack the
 * curve is drawn in; and the export rows.
 *
 * The panel adds no arithmetic. Every figure it shows is a field of `GET /forecast/projects/:id`,
 * and what this module decides is how the fields are said — which is why the sentences take the
 * app's formatters as arguments: the digest of Phase 36 can say the same panel in the same words.
 *
 * Pure: no DOM. Shared by the card, its tests and the server.
 */
import { MAX_TYPE_HUES, OTHER_SLOT, assignTypePalette } from './palette.js';
import { col } from './table.js';

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The filter-bar keys the panel reads (`apiParams`). The run, and nothing else. */
export const PROJECT_PARAM_KEYS = Object.freeze(['run_id']);

/** How many projects the picker asks for at once. */
export const PROJECT_LIST_LIMIT = 50;

// ---------------------------------------------------------------------------------------------
// Source mode

/**
 * Where a project's forecast came from, in the PRD sketch's words ("Source Mode: Historical
 * Estimate"). `sentence` says what the run did; `evidence` names the block beneath that shows it.
 */
export const SOURCE_MODE_META = Object.freeze(
  [
    {
      key: 'explicit',
      label: 'Workamajig assignments',
      sentence: 'The remaining hours of each assignment in Workamajig, spread over its schedule.',
      evidence: 'assignments',
    },
    {
      key: 'inferred_schedule',
      label: 'Assignments on an inferred schedule',
      sentence:
        'The assignments in Workamajig carry the hours, but the window was inferred from the project dates or the due date.',
      evidence: 'schedule',
    },
    {
      key: 'inferred_role_mix',
      label: 'Historical estimate',
      sentence:
        'No assignments in Workamajig: the hours were split across roles the way similar closed projects spent theirs.',
      evidence: 'analogs',
    },
    {
      key: 'recurring_baseline',
      label: 'Recurring baseline',
      sentence:
        'A retainer: the time logged over the trailing window, projected flat into every week of the horizon.',
      evidence: 'recurring',
    },
    {
      key: 'recurring_seasonal',
      label: 'Recurring, seasonal',
      sentence:
        'A retainer with enough history: the trailing window blended with the same weeks last year, so each week carries its own season rather than the mean.',
      evidence: 'recurring',
    },
  ].map(Object.freeze),
);
const SOURCE_BY_KEY = new Map(SOURCE_MODE_META.map((m) => [m.key, m]));

/** The words for a source mode; an unknown mode reads as itself rather than vanishing. */
export function sourceMeta(mode) {
  return (
    SOURCE_BY_KEY.get(mode) ?? {
      key: mode,
      label: humanizeKey(mode),
      sentence: '',
      evidence: null,
    }
  );
}

/** The tier as the sketch says it: "medium confidence". */
export const TIER_WORDS = Object.freeze({
  high: 'high confidence',
  medium: 'medium confidence',
  low: 'low confidence',
});

/**
 * The source line: "Historical estimate · medium confidence, typically within 18%". The error
 * band is Phase 17's observed one for the tier or the mode, and until the accuracy job has
 * published one the line says so rather than inventing a figure (PRD 6.1).
 * @param {{ project: { source_mode: string, confidence_tier: string | null }, confidence_band: object | null }} data
 * @param {{ percent: (v: number) => string }} fmt
 */
export function sourceSentence(data, { percent }) {
  const mode = sourceMeta(data?.project?.source_mode);
  const tier = data?.project?.confidence_tier;
  const parts = [mode.label];
  if (tier) {
    const band = data.confidence_band;
    const within =
      band && band.ape_band !== null && band.ape_band !== undefined
        ? `, typically within ${percent(band.ape_band)}`
        : ', no observed error band yet';
    parts.push(`${TIER_WORDS[tier] ?? tier}${within}`);
  }
  return parts.join(' · ');
}

/**
 * What the band's figure means, for the hover and the table: "80% of measured weeks were within
 * 18% (240 project-weeks, by source mode)".
 */
export function bandSentence(band, { percent, count }) {
  if (!band || band.ape_band === null || band.ape_band === undefined) return null;
  const share = band.band_percentile !== null ? percent(band.band_percentile) : 'Most';
  const basis = band.basis === 'source_mode' ? 'by source mode' : 'by confidence tier';
  return `${share} of measured weeks were within ${percent(band.ape_band)} (${count(band.sample_count)} project-weeks, ${basis})`;
}

// ---------------------------------------------------------------------------------------------
// Remaining hours and burn

/**
 * The remaining line, as the sketch has it: "340 h of 520 h planned · 62% of the schedule
 * elapsed, 35% of the hours consumed". A recurring project has no plan to be remaining against,
 * so it says the baseline instead.
 * @param {{ remaining: object, project: { source_mode: string } }} data
 * @param {{ hours: Function, percent: Function }} fmt
 */
export function remainingSentence(data, { hours, percent }) {
  const r = data?.remaining ?? {};
  if (data?.project?.source_mode === 'recurring_baseline') {
    return `${hours(r.recurring_weekly_hours)} a week from the baseline · ${hours(r.hours_allocated)} in the horizon`;
  }
  const head = `${hours(r.hours_remaining)} of ${hours(r.hours_planned)} planned`;
  const tail = [];
  if (r.schedule_elapsed_fraction !== null && r.schedule_elapsed_fraction !== undefined)
    tail.push(`${percent(r.schedule_elapsed_fraction)} of the schedule elapsed`);
  if (r.hours_consumed_fraction !== null && r.hours_consumed_fraction !== undefined)
    tail.push(`${percent(r.hours_consumed_fraction)} of the hours consumed`);
  return tail.length ? `${head} · ${tail.join(', ')}` : head;
}

/**
 * The burn reading in a word. The engine's rule is one-sided — burn_risk is ratio above the
 * margin — and the panel says the other side too, because a project consuming a third of the
 * hours it should have is information as well. `none` when there is no ratio: no actuals, no
 * schedule, or a recurring project.
 */
export const BURN_STATES = Object.freeze([
  Object.freeze({ key: 'fast', label: 'Burning fast', marker: '!' }),
  Object.freeze({ key: 'steady', label: 'On pace', marker: null }),
  Object.freeze({ key: 'slow', label: 'Burning slowly', marker: null }),
  Object.freeze({ key: 'none', label: 'No burn reading', marker: null }),
]);
const BURN_BY_KEY = new Map(BURN_STATES.map((b) => [b.key, b]));

/**
 * @param {{ burn_ratio: number | null, burn_risk: boolean, burn_margin: number | null }} remaining
 * @param {number} [defaultMargin]  when the run stored none
 */
export function burnState(remaining, defaultMargin = 1.25) {
  const ratio = num(remaining?.burn_ratio);
  if (ratio === null) return BURN_BY_KEY.get('none');
  const margin = num(remaining?.burn_margin) ?? defaultMargin;
  if (remaining?.burn_risk || ratio > margin) return BURN_BY_KEY.get('fast');
  if (ratio < 1 / margin) return BURN_BY_KEY.get('slow');
  return BURN_BY_KEY.get('steady');
}

/**
 * "Burn ratio 1.53 — hours are going 53% faster than the schedule; inflated ×1.32 by the median
 * overrun of 10 analogs" — or "capped at 1.5× planned" when the cap bound.
 */
export function burnSentence(remaining, { percent, count }) {
  const ratio = num(remaining?.burn_ratio);
  if (ratio === null) return 'No burn ratio: nothing logged yet, or no schedule to pace against.';
  const state = burnState(remaining);
  const pace =
    state.key === 'fast'
      ? `hours are going ${percent(ratio - 1)} faster than the schedule`
      : state.key === 'slow'
        ? `hours are going ${percent(1 - ratio)} slower than the schedule`
        : 'hours and schedule are moving together';
  let s = `Burn ratio ${ratio.toFixed(2)} — ${pace}`;
  const factor = num(remaining?.inflation_factor);
  if (factor !== null && factor > 1) {
    const source = OVERRUN_SOURCE_WORDS[remaining.overrun_source] ?? remaining.overrun_source;
    s += remaining.inflation_capped
      ? `; remaining hours inflated ×${factor.toFixed(2)}, capped at the setting`
      : `; remaining hours inflated ×${factor.toFixed(2)} by the median overrun of ${count(remaining.overrun_analog_count)} ${source}`;
  }
  return `${s}.`;
}

const OVERRUN_SOURCE_WORDS = Object.freeze({
  analogs: 'analogs',
  canonical_type: 'closed projects of the type',
  all_closed: 'closed projects',
});

// ---------------------------------------------------------------------------------------------
// Schedule, slip and curve

/** Where the window came from (forecast_projects.schedule_source). */
export const SCHEDULE_SOURCE_WORDS = Object.freeze({
  schedule: 'from the Workamajig schedule',
  assignments: 'from the span of its assignments',
  project_dates: 'from the project start and due dates',
  inferred: 'from the due date less an inferred duration',
});

export const CURVE_WORDS = Object.freeze({
  front_loaded: 'front-loaded',
  even: 'even',
  end_loaded: 'end-loaded',
});

/** "May 20 – Mar 17, 2027 from the Workamajig schedule · even curve · due Mar 17, 2027". */
export function scheduleSentence(schedule, { mediumDate, count }) {
  const s = schedule ?? {};
  if (!s.start && !s.end) return 'No schedule: the hours have no window to be spread over.';
  const span = `${s.start ? mediumDate(s.start) : '…'} – ${s.end ? mediumDate(s.end) : '…'}`;
  const parts = [`${span} ${SCHEDULE_SOURCE_WORDS[s.source] ?? ''}`.trim()];
  if (s.source === 'inferred' && s.duration_weekdays)
    parts.push(
      `${count(s.duration_weekdays)} weekdays, the median of ${count(s.duration_analog_count)} closed ${DURATION_SOURCE_WORDS[s.duration_source] ?? 'projects'}`,
    );
  if (s.curve) parts.push(`${CURVE_WORDS[s.curve] ?? s.curve} curve`);
  if (s.target_delivery_date) parts.push(`due ${mediumDate(s.target_delivery_date)}`);
  return parts.join(' · ');
}

const DURATION_SOURCE_WORDS = Object.freeze({
  type_tier: 'projects of the type and tier',
  canonical_type: 'projects of the type',
  all_closed: 'projects',
  default: 'projects (the default)',
});

/**
 * The slip in one sentence, or null when nothing slipped. Phase 9 re-anchors work whose window
 * has passed to next planning week, and `slipped_from` is the earliest planned start among the
 * re-anchored assignments (the date the timeline draws the ghost from); a project past its own
 * due date has all of its remaining work moved.
 */
export function slipSentence(schedule, { hours, count, mediumDate }) {
  const s = schedule ?? {};
  if (!s.slipped_from && !s.project_past_due) return null;
  const parts = [];
  if (s.slipped_from)
    parts.push(
      `${hours(s.hours_slipped)} on ${count(s.assignments_slipped)} ${s.assignments_slipped === 1 ? 'assignment' : 'assignments'} re-anchored to next planning week; their windows began as early as ${mediumDate(s.slipped_from)}`,
    );
  if (s.project_past_due)
    parts.push(
      `the project's due date (${mediumDate(s.target_delivery_date)}) has passed, so remaining work starts next planning week`,
    );
  const text = parts.join('; ');
  return text.charAt(0).toUpperCase() + text.slice(1) + '.';
}

// ---------------------------------------------------------------------------------------------
// Flags

/**
 * The words for every assumption flag the run can put on a project or its rows (run.js). A
 * test holds this list to the engine's, so a flag added there cannot reach the panel as a
 * `snake_case` token. `where` names the system a fix is made in, as the hygiene worklist does.
 */
export const FLAG_META = Object.freeze(
  [
    {
      flag: 'missing_assignments',
      label: 'No assignments in Workamajig',
      sentence: 'Nobody is assigned, so the role split comes from similar closed projects.',
      where: 'workamajig',
    },
    {
      flag: 'inferred_role_mix',
      label: 'Role mix inferred',
      sentence: 'The hours were split across roles the way the analogs spent theirs.',
      where: 'waag',
    },
    {
      flag: 'role_only',
      label: 'Role, not a person',
      sentence:
        'Demand is on a role nobody is named for yet, so it counts for the company and the service but for no one’s week.',
      where: 'workamajig',
    },
    {
      flag: 'missing_schedule',
      label: 'No schedule',
      sentence: 'The window was generated from the due date and an inferred duration.',
      where: 'workamajig',
    },
    {
      flag: 'inferred_schedule',
      label: 'Schedule inferred',
      sentence: 'The window is provisional: remaining hours follow the labour curve over it.',
      where: 'workamajig',
    },
    {
      flag: 'window_from_task',
      label: 'Window from the task',
      sentence: 'The assignment carries no dates; its task’s were used.',
      where: 'workamajig',
    },
    {
      flag: 'window_from_project',
      label: 'Window from the project schedule',
      sentence:
        'Neither the assignment nor its task carries dates; the project schedule’s were used.',
      where: 'workamajig',
    },
    {
      flag: 'window_from_project_dates',
      label: 'Window from the project dates',
      sentence: 'There is no plan-level schedule; the project start and due dates were used.',
      where: 'workamajig',
    },
    {
      flag: 'window_from_inferred',
      label: 'Window inferred',
      sentence:
        'There is a due date and no start; the start is the due date less the inferred duration.',
      where: 'workamajig',
    },
    {
      flag: 'window_inverted',
      label: 'Window ends before it starts',
      sentence: 'The dates in Workamajig run backwards; the run treated them as one day.',
      where: 'workamajig',
    },
    {
      flag: 'hours_estimated',
      label: 'Hours estimated',
      sentence: 'No budget: the hours are the median actual hours of the analogs.',
      where: 'workamajig',
    },
    {
      flag: 'missing_budget',
      label: 'No budget, no estimate',
      sentence:
        'No budget and no analogs close enough to borrow hours from, so the project puts no demand in the forecast.',
      where: 'workamajig',
    },
    {
      flag: 'low_analog_count',
      label: 'Few analogs',
      sentence:
        'Fewer closed projects matched than the setting asks for, so the confidence is low.',
      where: 'waag',
    },
    {
      flag: 'unmapped_taxonomy',
      label: 'Unmapped project type',
      sentence: 'The Workamajig type maps to no canonical type, so no analogs were matched.',
      where: 'waag',
    },
    {
      flag: 'burn_risk',
      label: 'Burn risk',
      sentence:
        'Hours are being used faster than the schedule; remaining hours were inflated by the median overrun.',
      where: 'workamajig',
    },
    {
      flag: 'inflation_capped',
      label: 'Inflation capped',
      sentence: 'The overrun inflation hit the cap in settings.',
      where: 'waag',
    },
    {
      flag: 'slipped',
      label: 'Slipped',
      sentence: 'Work whose window has passed was re-anchored to next planning week.',
      where: 'workamajig',
    },
    {
      flag: 'project_past_due',
      label: 'Project past due',
      sentence: 'The project’s due date has passed; remaining work starts next planning week.',
      where: 'workamajig',
    },
    {
      flag: 'no_capacity',
      label: 'Assignee has no capacity',
      sentence:
        'The person assigned has no capacity row in the run, so the hours count and the utilization does not.',
      where: 'workamajig',
    },
    {
      flag: 'recurring',
      label: 'Recurring',
      sentence: 'Detected as recurring work: forecast from the baseline, not the plan.',
      where: 'waag',
    },
    {
      flag: 'recurring_baseline',
      label: 'Baseline row',
      sentence: 'A recurring unit: one person and service, flat across the horizon.',
      where: 'waag',
    },
    {
      flag: 'recurring_seasonal',
      label: 'Blended with last year',
      sentence:
        'The baseline was blended with the same weeks last year, at the configured weight (Phase 50).',
      where: 'waag',
    },
    {
      flag: 'recurring_seasonal_sparse',
      label: 'Too little of last year to blend',
      sentence:
        'Eligible on history, but too few of last year’s mapped weeks carry time for their zeros to be evidence: the trailing average stands.',
      where: 'waag',
    },
    {
      flag: 'recurring_manual',
      label: 'Recurring by override',
      sentence: 'A planner marked the project recurring.',
      where: 'waag',
    },
    {
      flag: 'recurring_plan_divergent',
      label: 'Baseline differs from the plan',
      sentence:
        'The trailing baseline and the open assignments disagree by more than the tolerance.',
      where: 'workamajig',
    },
    {
      flag: 'baseline_weeks_excluded',
      label: 'Weeks excluded from the baseline',
      sentence: 'Some weeks of the trailing window were excluded by a planner.',
      where: 'waag',
    },
    {
      flag: 'no_recurring_history',
      label: 'No history for a baseline',
      sentence: 'Recurring, with no time logged in the trailing window: no demand in the forecast.',
      where: 'workamajig',
    },
    {
      flag: 'past_target_delivery',
      label: 'Past the due date',
      sentence: 'The retainer is projected beyond its due date, because retainers renew.',
      where: 'workamajig',
    },
    {
      flag: 'scenario_role_mix',
      label: 'Role mix from a scenario',
      sentence:
        'A scenario re-split this project’s weeks across other services; the hours and the weeks are the base forecast’s, the roles are not.',
      where: 'waag',
    },
  ].map(Object.freeze),
);
const FLAG_BY_KEY = new Map(FLAG_META.map((f) => [f.flag, f]));
export const FLAG_KEYS = Object.freeze(FLAG_META.map((f) => f.flag));

/** The words for one flag; an unknown flag is humanised, never dropped. */
export function flagMeta(flag) {
  return FLAG_BY_KEY.get(flag) ?? { flag, label: humanizeKey(flag), sentence: '', where: null };
}

/** The flag lines, in the panel's order: the API's order, known flags first. */
export function flagLines(flags) {
  const list = (flags ?? []).map(flagMeta);
  return [
    ...list.filter((f) => FLAG_BY_KEY.has(f.flag)),
    ...list.filter((f) => !FLAG_BY_KEY.has(f.flag)),
  ];
}

// ---------------------------------------------------------------------------------------------
// Linked deals

/** The link methods (links.js LINK_METHODS), as the panel says them. */
export const LINK_METHOD_WORDS = Object.freeze({
  manual: 'linked by hand',
  matched_deal_id: 'deal id recorded on the project',
  matched_project_number: 'project number in the deal name',
  matched_name_client: 'matched by name and client',
  won_date_proximity: 'matched by client and won date',
});

/**
 * "hub:4471 · Superior Energy Intranet — deal id recorded on the project" (the sketch's
 * "Linked HubSpot deal: hub:4471 (matched by name and client)"), with the confidence when the
 * matcher's, since a hand link is always 1.
 */
export function dealSentence(deal) {
  const method = LINK_METHOD_WORDS[deal.link_method] ?? humanizeKey(deal.link_method);
  const conf =
    deal.link_method === 'manual' || deal.confidence === null || deal.confidence >= 1
      ? ''
      : ` (${Number(deal.confidence).toFixed(2)})`;
  return `${deal.source_deal_id} · ${deal.name} — ${method}${conf}`;
}

// ---------------------------------------------------------------------------------------------
// Recurring

/**
 * "Baseline 5.79 h a week: 69.5 h logged over 12 weeks (Jun 29 – Sep 20) by 5 people" and, when
 * open assignments exist, how the plan compares.
 */
export function recurringSentence(recurring, { hours, count, mediumDate }) {
  const r = recurring;
  if (!r) return null;
  const w = r.window ?? {};
  const used =
    r.weeks_excluded > 0
      ? `${count(r.weeks_used)} of ${count(w.weeks)} weeks (${count(r.weeks_excluded)} excluded)`
      : `${count(w.weeks)} weeks`;
  let s = `Baseline ${hours(r.weekly_hours)} a week: ${hours(r.hours_in_window)} logged over ${used} (${mediumDate(w.start)} – ${mediumDate(w.end)}) by ${count(r.people)} ${r.people === 1 ? 'person' : 'people'}`;
  if (r.explicit?.assignments_open > 0)
    s += `. The ${count(r.explicit.assignments_open)} open assignments say ${hours(r.explicit.weekly_hours)} a week over ${count(r.explicit.weeks)} weeks (ratio ${Number(r.calibration_ratio ?? 0).toFixed(2)})`;
  return `${s}.`;
}

// ---------------------------------------------------------------------------------------------
// Analogs

/** What an analog lent, as words. */
export function usedForWords(analog) {
  if (analog.used_for_mix && analog.used_for_overrun) return 'role mix and overrun';
  if (analog.used_for_mix) return 'role mix';
  if (analog.used_for_overrun) return 'overrun';
  return 'matched, not used';
}

/** The component names of a similarity score, in the TRD's order, for the hover and the table. */
export const SIMILARITY_COMPONENTS = Object.freeze([
  ['canonical_type', 'Type'],
  ['service_mix', 'Service mix'],
  ['scope_tier', 'Scope tier'],
  ['duration_band', 'Duration band'],
  ['client', 'Client'],
]);

// ---------------------------------------------------------------------------------------------
// Roles and the curve

export const MAX_ROLE_HUES = MAX_TYPE_HUES;
export const OTHER_ROLES = Object.freeze({
  key: 'other_roles',
  label: 'Other roles',
  token: OTHER_SLOT.var,
});

/**
 * The hue each role wears in the curve and the role list: the roles by share take the type
 * slots in slot order, the rest read as Other — the bench's own rule with share as the volume,
 * so the adjacent-pair validation carries over.
 * @param {Array<{ canonical_service: string, label: string, share: number, hours: number }>} rows
 */
export function roleSlots(rows) {
  const assigned = assignTypePalette(
    (rows ?? []).map((r) => ({
      value: r.canonical_service,
      label: r.label,
      volume: Math.max(num(r.share) ?? 0, num(r.hours) ?? 0),
      priority: 1,
    })),
  );
  return new Map(assigned.map((a) => [a.value, { slot: a.slot, hue: a.hue, token: a.var }]));
}

/** The segments the stack is drawn in, bottom-up: the hued roles in order, then the fold. */
export function foldRoles(rows) {
  const slots = roleSlots(rows);
  const drawn = [];
  const folded = [];
  for (const r of rows ?? []) {
    const s = slots.get(r.canonical_service);
    if (s && s.slot <= MAX_ROLE_HUES)
      drawn.push({ ...r, key: r.canonical_service, token: s.token });
    else folded.push({ ...r, key: r.canonical_service });
  }
  return { drawn, other: folded.length ? { ...OTHER_ROLES, roles: folded } : null };
}

/** One week's stack: `[from, to]` per segment, summing to the week's hours by construction. */
export function weekStack(week, fold) {
  let base = 0;
  const segment = (key, label, token, hours, extra = {}) => {
    const s = { key, label, token, from: base, to: round2(base + hours), hours, ...extra };
    base = s.to;
    return s;
  };
  const by = week?.by_service ?? {};
  const segments = fold.drawn.map((r) =>
    segment(r.key, r.label, r.token, round2(num(by[r.key]) ?? 0)),
  );
  if (fold.other) {
    const parts = fold.other.roles
      .map((r) => ({ key: r.key, label: r.label, hours: round2(num(by[r.key]) ?? 0) }))
      .filter((p) => p.hours > 0);
    segments.push(
      segment(
        fold.other.key,
        fold.other.label,
        fold.other.token,
        round2(parts.reduce((s, p) => s + p.hours, 0)),
        { roles: parts },
      ),
    );
  }
  // A service on no role row (it should not happen: the rows are built from the same rows) still
  // has to reach the top of the stack, or the bar would be shorter than the week says.
  const known = new Set([
    ...fold.drawn.map((r) => r.key),
    ...(fold.other?.roles ?? []).map((r) => r.key),
  ]);
  const rest = round2(
    Object.entries(by)
      .filter(([k]) => !known.has(k))
      .reduce((s, [, h]) => s + (num(h) ?? 0), 0),
  );
  if (rest > 0) segments.push(segment('unlisted', 'Unlisted', OTHER_SLOT.var, rest));
  return segments;
}

/** The top of the value axis: the largest week. */
export function curveScaleMax(weeks) {
  let max = 0;
  for (const w of weeks ?? []) max = Math.max(max, num(w.hours) ?? 0);
  return max;
}

// ---------------------------------------------------------------------------------------------
// The picker

/**
 * A row of the picker (`GET /forecast/runs/:id/projects`), with the words the list shows. The
 * API's order — hours allocated, largest first — is kept.
 */
export function projectListRows(projects) {
  return (projects ?? []).map((p) => ({
    project_id: p.project_id,
    number: p.source_project_number,
    name: p.name,
    client: p.client,
    canonical_type: p.canonical_type,
    included: p.included,
    exclusion_reason: p.exclusion_reason,
    source_mode: p.source_mode,
    source_label: sourceMeta(p.source_mode).label,
    confidence_tier: p.confidence_tier,
    hours_remaining: p.hours_remaining,
    hours_allocated: p.hours_allocated,
    slipped: p.hours_slipped > 0,
    burn_risk: p.burn_risk,
    flags: p.flags?.length ?? 0,
    analog_count: p.analog_count,
  }));
}

export const EXCLUSION_WORDS = Object.freeze({
  type_not_forecast: 'type is not forecast',
  recurring: 'recurring',
  no_assignments: 'no assignments and no analogs',
  no_analogs: 'no assignments and no analogs',
  recurring_no_history: 'recurring, with no history',
});

// ---------------------------------------------------------------------------------------------
// Exports

export function weekExportRows(data, fmt = {}) {
  const roles = data?.roles?.rows ?? [];
  return (data?.weeks ?? []).map((w) => {
    const row = { week_start: w.week_start, hours: w.hours };
    for (const r of roles)
      row[r.canonical_service] = round2(num(w.by_service?.[r.canonical_service]) ?? 0);
    return fmt.week ? { ...row, week: fmt.week(w.week_start) } : row;
  });
}

export function roleExportRows(data) {
  return (data?.roles?.rows ?? []).map((r) => ({
    canonical_service: r.canonical_service,
    label: r.label,
    share: r.share,
    hours: r.hours,
    weekly_hours: r.weekly_hours,
    source: data.roles.source,
  }));
}

export function analogExportRows(data) {
  return (data?.analogs ?? []).map((a) => ({
    rank: a.rank,
    number: a.source_project_number,
    name: a.name,
    client: a.client,
    canonical_type: a.canonical_type,
    similarity: a.similarity,
    used_for: usedForWords(a),
    same_client: a.same_client,
    hours_planned: a.hours_planned,
    hours_actual: a.hours_actual,
    overrun_ratio: a.overrun_ratio,
    scope_tier: a.scope_tier,
    duration_band: a.duration_band,
    completed_on: a.completed_on,
    mix_basis: a.mix_basis,
    ...Object.fromEntries(
      SIMILARITY_COMPONENTS.map(([k]) => [`component_${k}`, a.components?.[k] ?? null]),
    ),
  }));
}

export function assignmentExportRows(data) {
  return (data?.assignments ?? []).map((a) => ({
    key: a.source_assignment_key,
    task: a.task_name,
    who: a.user?.display_name ?? a.assignee_name ?? (a.role ? `role: ${a.role}` : '—'),
    canonical_service: a.canonical_service,
    hours_planned: a.hours_planned,
    hours_actual: a.hours_actual,
    hours_remaining: a.hours_remaining,
    hours_remaining_adjusted: a.hours_remaining_adjusted,
    hours_allocated: a.hours_allocated,
    window_start: a.window_start,
    window_end: a.window_end,
    window_source: a.window_source,
    curve: a.curve,
    slipped: a.slipped,
    slipped_from: a.slipped_from,
    confidence_tier: a.confidence_tier,
    source_mode: a.source_mode,
    flags: (a.assumption_flags ?? []).join(' '),
    weeks: a.weeks?.length ?? 0,
  }));
}

/** Title Case a snake_case key. */
export function humanizeKey(value) {
  if (!value) return '—';
  const s = String(value).replace(/[_-]+/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings: the screen decorates them with `withFormats` and the CSV
// writer reads the same list. The week table gains a column per role the project carries, so it
// takes the roles.

/**
 * One row per week: the project's hours, split by role.
 * @param {{ roles?: {canonical_service: string, label: string}[] }} opts
 */
export function projectWeekColumns({ roles = [] } = {}) {
  return [
    col.week('week_start', 'Week', { type: 'string' }),
    col.hours('hours', 'Hours'),
    ...roles.map((r) => col.hours(r.canonical_service, r.label)),
  ];
}

/** One row per role: how the project's hours are split, and where the split came from. */
export function projectRoleColumns() {
  return [
    col.text('label', 'Role', { type: 'string' }),
    col.text('canonical_service', 'Key', { type: 'string' }),
    col.percent('share', 'Share'),
    col.hours('hours', 'Hours in horizon'),
    col.hours('weekly_hours', 'Baseline h/week'),
    col.text('source', 'Source', { type: 'string' }),
  ];
}

/** One row per analog, with the components of its similarity score. */
export function projectAnalogColumns() {
  return [
    col.count('rank', '#'),
    col.text('number', 'Number', { type: 'string' }),
    col.text('name', 'Project', { type: 'string' }),
    col.text('client', 'Client', { type: 'string' }),
    col.number('similarity', 'Similarity'),
    col.text('used_for', 'Used for', { type: 'string' }),
    col.flag('same_client', 'Same client', { type: 'string' }),
    col.hours('hours_planned', 'Planned'),
    col.hours('hours_actual', 'Actual'),
    col.number('overrun_ratio', 'Overrun'),
    col.text('scope_tier', 'Tier', { type: 'string' }),
    col.text('duration_band', 'Band', { type: 'string' }),
    col.date('completed_on', 'Completed', { type: 'string' }),
    col.text('mix_basis', 'Mix from', { type: 'string' }),
    ...SIMILARITY_COMPONENTS.map(([k, label]) => col.number(`component_${k}`, label)),
  ];
}

/** One row per assignment: the plan behind the project's hours. */
export function projectAssignmentColumns() {
  return [
    col.text('task', 'Task', { type: 'string' }),
    col.text('who', 'Who', { type: 'string' }),
    col.text('canonical_service', 'Role', { type: 'string' }),
    col.hours('hours_planned', 'Planned'),
    col.hours('hours_actual', 'Actual'),
    col.hours('hours_remaining', 'Remaining'),
    col.hours('hours_allocated', 'In horizon'),
    col.date('window_start', 'From', { type: 'string' }),
    col.date('window_end', 'To', { type: 'string' }),
    col.text('window_source', 'Window from', { type: 'string' }),
    col.text('curve', 'Curve', { type: 'string' }),
    col.flag('slipped', 'Slipped', { type: 'string' }),
    col.date('slipped_from', 'Slipped from', { type: 'string' }),
    col.text('confidence_tier', 'Tier', { type: 'string' }),
    col.text('source_mode', 'Source', { type: 'string' }),
    col.text('flags', 'Flags', { type: 'string' }),
    col.count('weeks', 'Weeks'),
  ];
}
